import {
  InputGroup,
  NumericInputGroup,
  CheckboxGroup,
  Button,
  GenericCombobox
} from '@/common/components'
import useI18n from '@/shared/hooks/usei18n'
import { useCallback, useMemo, useState } from 'react'
import { NON_EU_COUNTRIES } from '@/common'
import UnlocodeInput from '@/features/suppliers/components/UnlocodeInput'
import { orderBy } from 'es-toolkit'

import {
  InstallationFormData,
  installationAddressSchema
} from '../../pages/supplier-details/components/InstallationForm.schema'
import z from 'zod'

type StateSchema = z.infer<typeof installationAddressSchema>

type Props = {
  data: InstallationFormData
  onNextClick: (data: StateSchema) => void
  onBackClick: () => void
  partnershipAddress?: {
    operatorStreet: string
    operatorStreetNumber: string
    operatorStreet2: string
    operatorPostCode: string
    operatorCity: string
    operatorPoBox: string
  }
}

export default function InstallationAddressStep({
  data,
  onNextClick,
  onBackClick,
  partnershipAddress
}: Props) {
  const { t } = useI18n()

  const countryOptions = useMemo(() => {
    return orderBy(
      NON_EU_COUNTRIES.map(country => ({
        value: country.value,
        label: t(`common:countriesByIsoAlpha2:${country.value}`)
      })),
      [option => option.label.toLowerCase().normalize('NFKD')],
      ['asc']
    )
  }, [t])

  const [usePartnershipAddress, setUsePartnershipAddress] = useState(true)
  const [state, setState] = useState<StateSchema>({
    street: data.street,
    streetNumber: data.streetNumber,
    street2: data.street2,
    postCode: data.postCode,
    city: data.city,
    poBox: data.poBox,
    country: data.country,
    unlocode: data.unlocode,
    longitude: data.longitude,
    latitude: data.latitude
  })
  const {
    street,
    streetNumber,
    street2,
    postCode,
    city,
    poBox,
    country,
    unlocode,
    longitude,
    latitude
  } = state

  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      if (checked && partnershipAddress) {
        setUsePartnershipAddress(true)

        setState(prev => ({
          ...prev,
          street: partnershipAddress.operatorStreet || '',
          streetNumber: partnershipAddress.operatorStreetNumber || '',
          street2: partnershipAddress.operatorStreet2 || '',
          postCode: partnershipAddress.operatorPostCode || '',
          city: partnershipAddress.operatorCity || '',
          poBox: partnershipAddress.operatorPoBox || ''
        }))

        return
      }
      setUsePartnershipAddress(false)

      setState(prev => ({
        ...prev,
        street: '',
        streetNumber: '',
        street2: '',
        postCode: '',
        city: '',
        poBox: ''
      }))
    },
    [partnershipAddress]
  )

  const handleInputChange = (
    evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) => {
    setState(prev => ({ ...prev, [evt.target.id]: evt.target.value }))
  }

  const handleUnlocodeChange = (value: string | null) => {
    setState(prev => ({ ...prev, unlocode: value || '' }))
  }

  const handleCountryChange = (value: string | null) => {
    setState(prev => ({ ...prev, country: value || '' }))
  }

  const handleNext = () => {
    onNextClick(state)
  }

  /* ────────────────── Validation helpers ────────────────── */
  const longitudeError = useMemo(() => {
    if (longitude === null || longitude === undefined || String(longitude).trim() === '') {
      return undefined
    }
    const numericValue = Number(longitude)
    return numericValue < -180 || numericValue > 180
      ? t('installations:installationInputFields.longitude.errors.outOfRange', {
          min: -180,
          max: 180
        })
      : undefined
  }, [longitude, t])

  const latitudeError = useMemo(() => {
    if (latitude === null || latitude === undefined || String(latitude).trim() === '') {
      return undefined
    }
    const numericValue = Number(latitude)
    return numericValue < -90 || numericValue > 90
      ? t('installations:installationInputFields.latitude.errors.outOfRange', {
          min: -90,
          max: 90
        })
      : undefined
  }, [latitude, t])

  const validation = installationAddressSchema.safeParse(state)
  const fieldsErrorDisplayMap = useMemo(() => {
    const fieldErrors = validation.error ? z.flattenError(validation.error).fieldErrors : {}
    function getError(field: string) {
      return fieldErrors[field as keyof typeof fieldErrors]?.join(', ') ?? undefined
    }
    return {
      street: getError('street'),
      streetNumber: getError('streetNumber'),
      postCode: getError('postCode'),
      city: getError('city'),
      poBox: getError('poBox'),
      street2: getError('street2')
    }
  }, [validation])
  return (
    <div className="space-y-6 pb-2">
      <h2 className="text-xl font-semibold mb-2">
        {t('installations:installationInputFields.sections.address')}
      </h2>
      <p className="text-sm text-text-secondary mb-6">
        {t('installations:createInstallationDialog.steps.descriptions.address')}
      </p>
      <div className="space-y-4">
        {partnershipAddress !== undefined && (
          <div className="mb-4">
            <CheckboxGroup
              id="usePartnershipAddress"
              checked={usePartnershipAddress}
              onChange={handleCheckboxChange}
              label={t('installations:installationInputFields.useSupplierAddress')}
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <InputGroup
            id="street"
            error={fieldsErrorDisplayMap.street}
            label={t('common:inputFields.street.label')}
            value={street}
            onChange={handleInputChange}
          />
          <InputGroup
            id="streetNumber"
            error={fieldsErrorDisplayMap.streetNumber}
            label={t('common:inputFields.streetNumber.label')}
            value={streetNumber}
            onChange={handleInputChange}
          />
        </div>

        <InputGroup
          id="street2"
          label={t('common:inputFields.street2.label')}
          value={street2}
          onChange={handleInputChange}
          error={fieldsErrorDisplayMap.street2}
        />

        <div className="grid md:grid-cols-3 gap-4">
          <InputGroup
            id="postCode"
            label={t('common:inputFields.postCode.label')}
            value={postCode}
            onChange={handleInputChange}
            required={
              NON_EU_COUNTRIES.find(countryItem => countryItem.value === country)
                ?.isPostCodeRequired
            }
            error={fieldsErrorDisplayMap.postCode}
          />
          <InputGroup
            id="city"
            label={t('common:inputFields.city.label')}
            value={city}
            onChange={handleInputChange}
            required
            error={fieldsErrorDisplayMap.city}
          />
          <InputGroup
            id="poBox"
            label={t('common:inputFields.poBox.label')}
            value={poBox}
            onChange={handleInputChange}
            error={fieldsErrorDisplayMap.poBox}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <GenericCombobox
            required
            label={t('common:inputFields.country.label')}
            items={countryOptions}
            value={
              country
                ? { value: country, label: t(`common:countriesByIsoAlpha2:${country}`) }
                : null
            }
            onChange={evt => handleCountryChange(evt?.value || null)}
          />
          <UnlocodeInput
            countryCodeFilter={country}
            label={t('installations:installationInputFields.unlocode.label')}
            value={unlocode || ''}
            tooltip={t('installations:installationInputFields.unlocode.tooltip')}
            tooltipPlacement="top"
            onChange={handleUnlocodeChange}
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <NumericInputGroup
            id="longitude"
            label={t('installations:installationInputFields.longitude.label')}
            value={longitude ? longitude.toString() : ''}
            onChange={handleInputChange}
            tooltip={t('installations:installationInputFields.longitude.tooltip')}
            tooltipPlacement="top"
            error={longitudeError}
            numericFormat={{
              decimalScale: 6,
              min: -180,
              max: 180,
              fixedDecimalScale: true
            }}
          />
          <NumericInputGroup
            id="latitude"
            label={t('installations:installationInputFields.latitude.label')}
            value={latitude ? latitude.toString() : ''}
            onChange={handleInputChange}
            tooltip={t('installations:installationInputFields.latitude.tooltip')}
            tooltipPlacement="top"
            error={latitudeError}
            numericFormat={{
              decimalScale: 6,
              min: -90,
              max: 90,
              fixedDecimalScale: true
            }}
          />
        </div>
      </div>
      <div className="flex justify-between pt-16">
        <Button variant="neutral" onClick={onBackClick}>
          {t('common:actions.back')}
        </Button>
        <Button onClick={handleNext} disabled={!validation.success}>
          {t('common:actions.next')}
        </Button>
      </div>
    </div>
  )
}
